export interface CheckResult {
  checkId: string;
  displayName: string;
  status: 'PASS' | 'WARN' | 'FAIL' | 'ERROR' | 'SKIPPED' | 'MUTED';
  messageKey: string;
  messageParams: Record<string, string>;
  detail: Record<string, string>[];
  errorCodes: string[];
  group?: string | null;
  /** Severity cap from suite config. 'MUTED' = shown but excluded from the overall disposition. */
  severity?: 'PASS' | 'WARN' | 'FAIL' | 'ERROR' | 'SKIPPED' | 'MUTED';
}

export interface ContactSummary {
  firstName: string;
  lastName: string;
  state: string | null;
  submittedAt: string | null;
  firstPaymentDate: string | null;
  totalDebt: string;
  statusLabel?: string;
  stageLabel?: string | null;
  email?: string | null;
  phoneCell?: string | null;
  phoneHome?: string | null;
  marketingAgent?: string | null;
  assignedCompany?: string | null;
  servicingCompany?: string | null;
}

export interface QcRunResponse {
  runId: string;
  contactId: string;
  suiteId: string;
  suiteDisplayName?: string;
  overallStatus: 'PASS' | 'WARN' | 'FAIL' | 'ERROR';
  contactSummary?: ContactSummary;
  results: CheckResult[];
  executionTimeMs: number;
  forthApiRequests: number;
  timestamp: string;
  // Forth note-posting state (absent on runs never posted).
  notePosted?: boolean;
  noteId?: string | null;
  notePostedAt?: string;
  notePostedBy?: string;
  // N8N decision-tree trigger state. False/absent on a posted run whose workflow
  // trigger still needs a (note-safe) retry.
  workflowTriggered?: boolean;
}

export interface PostNoteResult {
  posted: boolean;
  /** True once the N8N decision-tree workflow was triggered. When posted is true but
   * this is false, the note is saved but the workflow needs a note-safe retry. */
  workflowTriggered?: boolean;
  /** Completion message(s) N8N returned; shown to the submitter as an info notice. */
  messages?: string[];
  error?: string;
  alreadyPosted?: boolean;
  noteId?: string | null;
  notePostedAt?: string;
  notePostedBy?: string;
}

export interface QcHistoryResponse {
  contactId: string;
  runs: QcRunResponse[];
}

export interface SuiteSummary {
  suiteId: string;
  displayName: string;
}

export async function getSuites(): Promise<SuiteSummary[]> {
  const res = await fetch(`${BASE}/qc/suites`);
  if (!res.ok) throw new Error('Failed to fetch suites');
  const data = await res.json() as { suites: SuiteSummary[] };
  return data.suites;
}

export interface SuiteDetection {
  planId: number | null;
  suiteId: string | null;
  displayName: string | null;
}

/** Pre-flight: resolve which suite a contact's enrollment plan belongs to. */
export async function detectSuite(contactId: string, signal?: AbortSignal): Promise<SuiteDetection> {
  const res = await fetch(`${BASE}/qc/detect-suite?contactId=${encodeURIComponent(contactId)}`, { signal });
  if (!res.ok) {
    checkAuth(res);
    throw new Error('Failed to detect suite');
  }
  return res.json();
}

const BASE = import.meta.env.VITE_API_URL ?? '';

// Auth event bus — fires 'unauthenticated' on 401 responses
export const authEvents = new EventTarget();

function checkAuth(res: Response): void {
  if (res.status === 401) {
    authEvents.dispatchEvent(new Event('unauthenticated'));
  }
}

// Auth types and functions

export interface AuthUser {
  sub: string;
  email: string;
  name: string;
}

export async function getLoginUrl(): Promise<string> {
  const res = await fetch(`${BASE}/auth/login`);
  if (!res.ok) throw new Error('Failed to get login URL');
  const data = await res.json() as { url: string };
  return data.url;
}


export async function getAuthMe(): Promise<AuthUser> {
  const res = await fetch(`${BASE}/auth/me`);
  if (!res.ok) throw new Error('Not authenticated');
  const data = await res.json() as { user: AuthUser };
  return data.user;
}

export async function postLogout(): Promise<void> {
  await fetch(`${BASE}/auth/logout`, { method: 'POST' });
}

// ── Magic-link API functions ────────────────────────────────────────────────
// NOTE: The token id is held in the httpOnly `ml_pending` cookie set by the
// server. JS never sees or handles it — only the 6-digit code travels in JS.

export interface MagicLinkRequestResult {
  ok: boolean;
  message?: string;
  error?: string;
  /** HTTP status code when ok is false — callers can use this to map to i18n keys. */
  status?: number;
}

/**
 * Request a magic-link + 6-digit code to be emailed to `email`.
 * `next` is the deep-link path to return to after the email-link sign-in (the
 * in-tab code path preserves the URL already); validated + sanitised server-side.
 */
export async function postMagicLinkRequest(email: string, next?: string): Promise<MagicLinkRequestResult> {
  const res = await fetch(`${BASE}/auth/magic-link/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, ...(next ? { next } : {}) }),
  });
  const data = await res.json().catch(() => ({})) as { ok?: boolean; message?: string; error?: string };
  if (!res.ok) {
    return { ok: false, status: res.status, error: data.error ?? 'Failed to send sign-in link' };
  }
  return { ok: true, message: data.message };
}

export interface MagicLinkVerifyResult {
  ok: boolean;
  error?: string;
}

/**
 * Verify the 6-digit code. The token id is read from the httpOnly `ml_pending`
 * cookie server-side — JS only sends the code. On success the server sets the
 * session `token` cookie.
 */
export async function postMagicLinkVerify(code: string): Promise<MagicLinkVerifyResult> {
  const res = await fetch(`${BASE}/auth/magic-link/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  const data = await res.json().catch(() => ({})) as { ok?: boolean; error?: string };
  if (!res.ok) {
    return { ok: false, error: data.error ?? 'Invalid code' };
  }
  return { ok: true };
}

export async function runQcCheck(contactId: string, suiteId = 'advocacy24'): Promise<QcRunResponse> {
  const res = await fetch(`${BASE}/qc/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contactId, suiteId }),
  });
  if (!res.ok) {
    checkAuth(res);
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'QC run failed');
  }
  return res.json();
}

/**
 * Fetch a single stored run by id, enriched server-side with a freshly-fetched
 * contact card. Used for deep links (/{suiteId}/{contactId}/{runId}) and history
 * selection so the view shows the customer name, not a bare contact id. No new run.
 */
export async function getRun(contactId: string, runId: string): Promise<QcRunResponse> {
  const res = await fetch(`${BASE}/qc/runs/${contactId}/${runId}`);
  if (!res.ok) {
    checkAuth(res);
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to fetch run');
  }
  return res.json();
}

export async function postRunNote(
  contactId: string,
  runId: string,
): Promise<PostNoteResult> {
  const res = await fetch(`${BASE}/qc/runs/${contactId}/${runId}/note`, { method: 'POST' });
  if (!res.ok) {
    checkAuth(res);
    throw new Error('Request failed');
  }
  return res.json();
}

export async function getHistory(contactId: string, suiteId?: string): Promise<QcHistoryResponse> {
  const query = suiteId ? `?suiteId=${encodeURIComponent(suiteId)}` : '';
  const res = await fetch(`${BASE}/qc/history/${contactId}${query}`);
  if (!res.ok) {
    checkAuth(res);
    throw new Error('Failed to fetch history');
  }
  return res.json();
}

// ── Admin API functions ───────────────────────────────────────────────────────
// All calls go to /admin/* which is gated server-side by requireAuth +
// requireAdmin. Cookies are sent automatically (same-origin). The server's
// Origin check passes because these requests come from the same origin.

export interface AdminCompany {
  companyId: string;
  name: string;
  domains: string[];
  emails: string[];
  createdAt: string;
}

/** List all companies. Returns 401 if not authenticated, 403 if not admin. */
export async function adminListCompanies(): Promise<{ ok: true; companies: AdminCompany[] } | { ok: false; status: number; error: string }> {
  const res = await fetch(`${BASE}/admin/companies`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { error?: string };
    return { ok: false, status: res.status, error: data.error ?? 'Failed to list companies' };
  }
  const data = await res.json() as { companies: AdminCompany[] };
  return { ok: true, companies: data.companies };
}

/** Create a new company. */
export async function adminCreateCompany(name: string): Promise<{ ok: true; company: AdminCompany } | { ok: false; error: string }> {
  const res = await fetch(`${BASE}/admin/companies`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  const data = await res.json().catch(() => ({})) as { company?: AdminCompany; error?: string };
  if (!res.ok) return { ok: false, error: data.error ?? 'Failed to create company' };
  return { ok: true, company: data.company! };
}

/** Delete a company by ID. */
export async function adminDeleteCompany(companyId: string): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(`${BASE}/admin/companies/${encodeURIComponent(companyId)}`, {
    method: 'DELETE',
  });
  const data = await res.json().catch(() => ({})) as { ok?: boolean; error?: string };
  if (!res.ok) return { ok: false, error: data.error ?? 'Failed to delete company' };
  return { ok: true };
}

/** Add an allowed domain to a company. */
export async function adminAddDomain(companyId: string, domain: string): Promise<{ ok: true; company: AdminCompany } | { ok: false; error: string }> {
  const res = await fetch(`${BASE}/admin/companies/${encodeURIComponent(companyId)}/domains`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ domain }),
  });
  const data = await res.json().catch(() => ({})) as { company?: AdminCompany; error?: string };
  if (!res.ok) return { ok: false, error: data.error ?? 'Failed to add domain' };
  return { ok: true, company: data.company! };
}

/** Remove an allowed domain from a company. */
export async function adminRemoveDomain(companyId: string, domain: string): Promise<{ ok: true; company: AdminCompany } | { ok: false; error: string }> {
  const res = await fetch(`${BASE}/admin/companies/${encodeURIComponent(companyId)}/domains`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ domain }),
  });
  const data = await res.json().catch(() => ({})) as { company?: AdminCompany; error?: string };
  if (!res.ok) return { ok: false, error: data.error ?? 'Failed to remove domain' };
  return { ok: true, company: data.company! };
}

/** Add a specific allowed email to a company. */
export async function adminAddEmail(companyId: string, email: string): Promise<{ ok: true; company: AdminCompany } | { ok: false; error: string }> {
  const res = await fetch(`${BASE}/admin/companies/${encodeURIComponent(companyId)}/emails`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({})) as { company?: AdminCompany; error?: string };
  if (!res.ok) return { ok: false, error: data.error ?? 'Failed to add email' };
  return { ok: true, company: data.company! };
}

/** Remove a specific allowed email from a company. */
export async function adminRemoveEmail(companyId: string, email: string): Promise<{ ok: true; company: AdminCompany } | { ok: false; error: string }> {
  const res = await fetch(`${BASE}/admin/companies/${encodeURIComponent(companyId)}/emails`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({})) as { company?: AdminCompany; error?: string };
  if (!res.ok) return { ok: false, error: data.error ?? 'Failed to remove email' };
  return { ok: true, company: data.company! };
}
